import React, { useState } from 'react';
import { getApiEndpoint } from '../../utils/config';
import './AirbnbImportPanel.css';

// Configurazione locale per Airbnb (sostituisce import mancante)
const AIRBNB_CONFIG = {
  maxRetries: 3,
  timeout: 15000,
  rateLimit: {
    minDelay: 3000,
    maxDelay: 8000
  }
};

interface ImportStats {
  method: string;
  success: boolean;
  totalAttempted: number;
  totalImported: number;
  source_url?: string;
  extracted_at: string;
  data_quality?: string;
  fallback_reason?: string; // Added for fallback reasons
}

interface Listing {
  id: string;
  title_it: string;
  title_en: string;
  price_per_night: number;
  city: string;
  address: string;
  rating: number;
  airbnb_id: string;
  external_data?: any;
  location_it?: string; // Added for location_it
}

export default function AirbnbImportPanel() {
  // Stati per import multiplo (esistente)
  const [userUrl, setUserUrl] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<Listing[]>([]);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [error, setError] = useState('');

  // Stati per import singolo (nuovo)
  const [singleListingUrl, setSingleListingUrl] = useState('');
  const [isSingleImporting, setIsSingleImporting] = useState(false);
  const [singleImportResult, setSingleImportResult] = useState<Listing | null>(null);
  const [singleImportStats, setSingleImportStats] = useState<ImportStats | null>(null);
  const [singleError, setSingleError] = useState('');

  // Stato connessione backend
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  // Verifica connessione backend all'avvio
  React.useEffect(() => {
    checkBackendConnection();
  }, []);

  const checkBackendConnection = async () => {
    try {
      const response = await fetch(getApiEndpoint('/api/health'), {
        method: 'GET',
        timeout: 5000
      } as any);
      
      if (response.ok) {
        setBackendStatus('online');
        console.log('✅ Backend online e raggiungibile');
      } else {
        setBackendStatus('offline');
      }
    } catch (error) {
      setBackendStatus('offline');
      console.log('⚠️ Backend offline');
    }
  };

  // Funzione per validare URL Airbnb
  const validateAirbnbUrl = (url: string): { isValid: boolean; error?: string } => {
    if (!url.trim()) {
      return { isValid: false, error: 'URL richiesto' };
    }

    // Pattern per URL Airbnb validi
    const airbnbUrlPattern = /^https?:\/\/(www\.)?(airbnb\.(it|com|co\.uk|fr|de|es))\/(rooms|experiences)\/\d+/i;
    
    if (!airbnbUrlPattern.test(url)) {
      return { 
        isValid: false, 
        error: 'URL non valido. Usa formato: https://www.airbnb.it/rooms/12345678' 
      };
    }

    return { isValid: true };
  };

  // Funzione per estrarre ID da URL (frontend)
  const extractIdFromUrl = (url: string): string | null => {
    const patterns = [
      /\/rooms\/(\d+)/,
      /\/experiences\/(\d+)/,
      /listing_id[=:](\d+)/i
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // 🎯 IMPORT SINGOLO ANNUNCIO REALE
  const handleSingleImport = async () => {
    setSingleError('');
    setSingleImportResult(null);
    setSingleImportStats(null);

    if (backendStatus !== 'online') {
      setSingleError('Backend non disponibile. Assicurati che il server Node.js sia in esecuzione sulla porta 3001.');
      return;
    }

    // Validazione URL
    const validation = validateAirbnbUrl(singleListingUrl);
    if (!validation.isValid) {
      setSingleError(validation.error || 'URL non valido');
      return;
    }

    const listingId = extractIdFromUrl(singleListingUrl);
    if (!listingId) {
      setSingleError('Impossibile estrarre ID dall\'URL');
      return;
    }

    setIsSingleImporting(true);

    try {
      console.log('🎯 Importando annuncio reale:', singleListingUrl);

      const response = await fetch(getApiEndpoint('/api/import-single-airbnb-listing'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          listingUrl: singleListingUrl.trim() 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Errore HTTP ${response.status}`);
      }

      if (data.success && data.listing) {
        setSingleImportResult(data.listing);
        setSingleImportStats(data.stats);
        
        // Auto-clear URL su successo
        setSingleListingUrl('');
        
        console.log('✅ Annuncio importato con successo:', data.listing.title_it);
      } else {
        throw new Error(data.error || 'Risposta inaspettata dal server');
      }

    } catch (error: any) {
      console.error('❌ Errore import singolo:', error);
      setSingleError(error.message || 'Errore durante l\'importazione');
    } finally {
      setIsSingleImporting(false);
    }
  };

  // 📚 IMPORT MULTIPLO REALE
  const handleImport = async () => {
    setError('');
    setImportResults([]);
    setImportStats(null);

    if (backendStatus !== 'online') {
      setError('Backend non disponibile. Riprova tra poco.');
      return;
    }

    setIsImporting(true);

    try {
      console.log('📚 Importando da profilo utente:', userUrl);

      const response = await fetch(getApiEndpoint('/api/import-airbnb-listings'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userUrl })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Errore HTTP ${response.status}`);
      }

      if (data.success) {
        setImportResults(data.listings || []);
        setImportStats(data.stats);
      } else {
        throw new Error(data.error || 'Importazione fallita');
      }

    } catch (error: any) {
      console.error('❌ Errore importazione multipla:', error);
      setError(error.message || 'Errore durante l\'importazione');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="airbnb-import-panel">
      <h2>🏠 Importazione Airbnb - Sistema Reale</h2>
      
      {/* Status Backend */}
      <div className={`backend-status ${backendStatus}`}>
        <div className="status-indicator">
          {backendStatus === 'checking' && '🔍 Verificando backend...'}
          {backendStatus === 'online' && '✅ Backend online - Pronto per import reali'}
          {backendStatus === 'offline' && '❌ Backend offline - Avvia server Node.js'}
        </div>
        {backendStatus === 'offline' && (
          <div className="status-help">
            <p>Per avviare il backend: <code>node server.js</code> in un terminale separato</p>
            <button onClick={checkBackendConnection} className="btn btn-secondary btn-small">
              🔄 Ricontrolla
            </button>
          </div>
        )}
      </div>
      
      {/* 🎯 SEZIONE IMPORT SINGOLO (Raccomandato) */}
      <div className="import-section single-import">
        <div className="section-header">
          <h3>📍 Import Singolo Annuncio (Raccomandato)</h3>
          <span className="badge recommended">Anti-Bot Friendly</span>
        </div>
        
        <div className="section-description">
          <p>🛡️ <strong>Metodo sicuro:</strong> Importa un annuncio alla volta per evitare blocchi anti-bot</p>
          <p>💡 <strong>Come usare:</strong> Copia l'URL di un singolo annuncio Airbnb</p>
          <p>⚡ <strong>Sistema reale:</strong> Estrae dati veri da Airbnb con anti-bot protection</p>
        </div>

        <div className="input-group">
          <label htmlFor="single-listing-url">URL Annuncio Airbnb</label>
          <input
            id="single-listing-url"
            type="url"
            value={singleListingUrl}
            onChange={(e) => setSingleListingUrl(e.target.value)}
            placeholder="https://www.airbnb.it/rooms/1313589238790816044"
            className="url-input"
            disabled={isSingleImporting || backendStatus !== 'online'}
          />
          <div className="input-help">
            ✅ Prova con: https://www.airbnb.it/rooms/1313589238790816044
          </div>
        </div>

        <div className="actions">
          <button 
            onClick={handleSingleImport}
            disabled={isSingleImporting || !singleListingUrl.trim() || backendStatus !== 'online'}
            className="btn btn-primary"
          >
            {isSingleImporting ? '⏳ Importando da Airbnb...' : '🎯 Importa Annuncio Reale'}
          </button>
          
          {singleListingUrl && (
            <button 
              onClick={() => setSingleListingUrl('')}
              disabled={isSingleImporting}
              className="btn btn-secondary"
            >
              🗑️ Pulisci
            </button>
          )}
        </div>

        {/* Risultati Import Singolo */}
        {singleError && (
          <div className="alert alert-error">
            <strong>❌ Errore:</strong> {singleError}
            <div className="error-suggestions">
              <p>💡 <strong>Suggerimenti:</strong></p>
              <ul>
                <li>Verifica che l'URL sia corretto e completo</li>
                <li>Assicurati che l'annuncio sia pubblico e attivo</li>
                <li>Se errore anti-bot, aspetta 5-10 minuti e riprova</li>
                <li>Controlla che il server backend sia avviato</li>
              </ul>
            </div>
          </div>
        )}

        {singleImportResult && (
          <div className="import-result success">
            <h4>✅ {singleImportStats?.data_quality === 'demo' ? 'Fallback Attivato' : 'Annuncio Importato da Airbnb'}</h4>
            
            {singleImportStats?.data_quality === 'demo' && (
              <div className="fallback-notice">
                <p>🛡️ <strong>Sistema Anti-Bot Rilevato:</strong> Airbnb ha bloccato l'accesso automatico.</p>
                <p>💡 <strong>Fallback Attivo:</strong> Dati demo generati per continuità del servizio.</p>
                <p>⏰ <strong>Riprova dopo:</strong> 1-2 ore per import reali.</p>
              </div>
            )}
            
            <div className="listing-card">
              <div className="listing-header">
                <h5>{singleImportResult.title_it}</h5>
                <span className="price">€{singleImportResult.price_per_night}/notte</span>
              </div>
              <div className="listing-details">
                <p><strong>📍 Località:</strong> {singleImportResult.city || singleImportResult.location_it}</p>
                <p><strong>🏠 Indirizzo:</strong> {singleImportResult.address}</p>
                <p><strong>⭐ Rating:</strong> {singleImportResult.rating}/5</p>
                <p><strong>🆔 ID Airbnb:</strong> {singleImportResult.airbnb_id}</p>
                <p><strong>🔗 Fonte:</strong> {singleImportStats?.data_quality === 'demo' ? 'Dati demo (Anti-bot fallback)' : 'Dati reali da Airbnb.it'}</p>
              </div>
            </div>
            
            {singleImportStats && (
              <div className="import-stats">
                <p><strong>📊 Metodo:</strong> {singleImportStats.method}</p>
                <p><strong>✅ Qualità:</strong> {singleImportStats.data_quality === 'demo' ? 'Demo (Fallback)' : 'Alta'}</p>
                <p><strong>🕐 Importato:</strong> {new Date(singleImportStats.extracted_at).toLocaleString('it-IT')}</p>
                {singleImportStats.source_url && (
                  <p><strong>🔗 URL Fonte:</strong> <a href={singleImportStats.source_url} target="_blank" rel="noopener noreferrer">Visualizza</a></p>
                )}
                {singleImportStats.fallback_reason && (
                  <p><strong>🛡️ Motivo Fallback:</strong> {singleImportStats.fallback_reason}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="section-divider">
        <span>oppure</span>
      </div>

      {/* 📚 SEZIONE IMPORT MULTIPLO (Rischio Anti-Bot) */}
      <div className="import-section multi-import">
        <div className="section-header">
          <h3>📚 Import Multiplo (Rischio Anti-Bot)</h3>
          <span className="badge warning">Attenzione</span>
        </div>
        
        <div className="section-description">
          <p>⚠️ <strong>Rischio blocchi:</strong> Questo metodo può triggerare i sistemi anti-bot</p>
          <p>🔍 <strong>Come funziona:</strong> Estrae tutti gli annunci dal profilo di un host</p>
          <p>⚡ <strong>Sistema reale:</strong> Scraping avanzato con protezioni anti-bot</p>
        </div>

        <div className="input-group">
          <label htmlFor="user-url">URL Profilo Host Airbnb</label>
          <input
            id="user-url"
            type="url"
            value={userUrl}
            onChange={(e) => setUserUrl(e.target.value)}
            placeholder="https://www.airbnb.it/users/show/123456789"
            className="url-input"
            disabled={isImporting || backendStatus !== 'online'}
          />
        </div>

        <div className="actions">
          <button 
            onClick={handleImport}
            disabled={isImporting || !userUrl.trim() || backendStatus !== 'online'}
            className="btn btn-warning"
          >
            {isImporting ? '⏳ Importando da Airbnb...' : '📚 Importa Tutti gli Annunci'}
          </button>
        </div>

        {/* Risultati Import Multiplo */}
        {error && (
          <div className="alert alert-error">
            <strong>❌ Errore:</strong> {error}
            <div className="error-suggestions">
              <p>💡 <strong>Suggerimenti per import multiplo:</strong></p>
              <ul>
                <li>Se anti-bot, usa import singolo invece</li>
                <li>Aspetta 1 ora prima di ritentare</li>
                <li>Verifica che l'host abbia annunci pubblici</li>
              </ul>
            </div>
          </div>
        )}

        {importResults.length > 0 && (
          <div className="import-result success">
            <h4>✅ Importazione Reale Completata</h4>
            <p><strong>📊 Annunci importati:</strong> {importResults.length}</p>
            
            <div className="listings-grid">
              {importResults.map((listing, index) => (
                <div key={index} className="listing-card mini">
                  <h6>{listing.title_it}</h6>
                  <p>€{listing.price_per_night}/notte - {listing.city}</p>
                  <small>🔗 Da Airbnb.it</small>
                </div>
              ))}
            </div>

            {importStats && (
              <div className="import-stats">
                <p><strong>📈 Successo:</strong> {importStats.totalImported}/{importStats.totalAttempted}</p>
                <p><strong>🕐 Completato:</strong> {new Date(importStats.extracted_at).toLocaleString('it-IT')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer con consigli */}
      <div className="panel-footer">
        <div className="tips">
          <h4>💡 Best Practices Anti-Bot (Sistema Reale):</h4>
          <ul>
            <li><strong>🎯 Preferisci Import Singolo:</strong> 0% rischio blocchi</li>
            <li><strong>⏱️ Tempi di attesa:</strong> 2-3 minuti tra import</li>
            <li><strong>📊 Limiti giornalieri:</strong> Max 10-15 annunci/giorno</li>
            <li><strong>🔄 Se bloccato:</strong> Aspetta 1 ora, poi usa solo import singolo</li>
          </ul>
        </div>

        <div className="system-info">
          <h4>🔧 Informazioni Sistema:</h4>
          <ul>
            <li><strong>Backend:</strong> Node.js con Express (porta 3001)</li>
            <li><strong>Protezioni:</strong> Headers realistici, retry logic, rate limiting</li>
            <li><strong>Database:</strong> Supabase (richiede colonna 'address')</li>
            <li><strong>Logging:</strong> Console per debug e monitoraggio</li>
          </ul>
        </div>
      </div>
    </div>
  );
} 